import {ChangeDetectorRef, Component, ElementRef, inject, OnInit, ViewChild} from '@angular/core'
import dayjs from 'dayjs';
import {TypeToast} from 'src/app/shared/ui/toast/state/toast.state'
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms'
import {DelegationService} from 'src/app/shared/data-access/http/delegation.service'
import {CsvService} from 'src/app/shared/data-access/http/csv.service'
import {ToastrService} from 'ngx-toastr'
import {DelegationDto} from 'src/app/shared/models/delegationDto'
import {NgClass, NgIf} from '@angular/common'
import {DataImmuablesDomainService} from "../../../core/domain/data-immuables.domain";
import {ToastDomainService} from "../../../shared/ui/toast/domain/toast.domain";
import {BsDatepickerModule} from "ngx-bootstrap/datepicker";
import {ColDef, ITooltipParams, ValueGetterParams} from "ag-grid-community";
import {AgGridAngular} from "ag-grid-angular";
import {localeFr} from "../../../../assets/localization/ag-grid/ag-grid-locale-fr";

@Component({
    selector: 'app-exporter-delegations',
    templateUrl: './exporter-delegations.component.html',
    styleUrls: ['./exporter-delegations.component.scss'],
    imports: [
        FormsModule,
        ReactiveFormsModule,
        NgClass,
        NgIf,
        BsDatepickerModule,
        AgGridAngular
    ]
})
export class ExporterDelegationsComponent implements OnInit {

    @ViewChild('dp', { static: false }) dp!: ElementRef

    public dateDelegationForm: FormGroup = this.fb.group({
        dateDelegation: [null, Validators.required],
    })
    public dateDelegationFormIsSubmitting: boolean = false
    public exportIsSubmitting: boolean = false

    dateDelegation: string | undefined
    delegations: DelegationDto[] = []
    maxManagersLength: number = 0
    additionalColumns: number[] = []
    private dataImmuablesDomain = inject(DataImmuablesDomainService)
    private toastDomainService = inject(ToastDomainService)
    private cdr = inject(ChangeDetectorRef)

    public startManagerCalendar = this.getStartManagerCalendar()
    public endManagerCalendar = this.getEndManagerCalendar()

    public colDefs: ColDef[] = [];

    defaultColDef: ColDef = {
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        filter: 'agTextColumnFilter',
        filterParams: { maxNumConditions: 1, filterOptions: ['contains', 'equals', 'notContains', 'notEqual'] },
    }

    constructor(
        private fb: FormBuilder,
        private delegationService: DelegationService,
        private csvService: CsvService,
        private toastr: ToastrService
    ) {
        this.dataImmuablesDomain.setComite().subscribe()
    }

    ngOnInit(): void {
        this.buildColumnDefs();
    }

    openDatepicker() {
        this.dp.nativeElement.click()
    }

    validerDateDelegation(): void {
        this.dateDelegationFormIsSubmitting = true;
        this.dateDelegation = dayjs(this.dateDelegationForm.get('dateDelegation')?.value).format('YYYY-MM-DD');
        this.delegationService.listerDelegations(this.dateDelegation).subscribe({
            next: (listerDelegationResponse) => {
                try {
                    this.delegations = listerDelegationResponse || [];
                    this.maxManagersLength = this.delegations.length > 0 ? Math.max(
                        ...this.delegations.map((delegation) =>
                            delegation.managersHierarchique ? delegation.managersHierarchique.length : 0
                        )
                    ) : 0;
                    this.additionalColumns = Array.from({length: this.maxManagersLength}, (_, i) => i + 1);
                    this.buildColumnDefs();
                    const dateDelegationToaster = dayjs(this.dateDelegationForm.get('dateDelegation')?.value).format('DD/MM/YYYY');
                    this.toastDomainService.addToastMessage(
                        TypeToast.success,
                        'Affichage Délégation',
                        `L'affichage des délégations en date du ${dateDelegationToaster} a été effectué`
                    );
                } catch (e) {
                    console.error(e);
                    this.toastDomainService.addToastMessage(
                        TypeToast.danger,
                        'Affichage Délégation',
                        "Erreur lors du traitement des données"
                    );
                } finally {
                    this.dateDelegationFormIsSubmitting = false;
                    this.cdr.detectChanges();
                }
            },
            error: (err) => {
                console.error(err);
                this.dateDelegationFormIsSubmitting = false;
                this.toastDomainService.addToastMessage(
                    TypeToast.danger,
                    'Affichage Délégation',
                    "Erreur lors de la récupération des délégations"
                );
                this.cdr.detectChanges();
            }
        });
    }

    exporterCSV(): void {
        this.exportIsSubmitting = true;
        if (this.dateDelegation !== undefined) {
            this.delegationService.getCsv(this.dateDelegation).subscribe({
                next: (response) => {
                    try {
                        if (response && response.body != null) {
                            this.toastDomainService.addToastMessage(
                                TypeToast.success,
                                "Export Délégations",
                                "Le résultat de votre export vous sera adressé d’ici quelques instants par mail au format Google Sheet."
                            );
                        } else {
                            this.toastDomainService.addToastMessage(
                                TypeToast.danger,
                                "Export Délégations",
                                "Erreur lors de l'export des délégations"
                            );
                        }
                    } catch (e) {
                        console.error(e);
                    } finally {
                        this.exportIsSubmitting = false;
                        this.cdr.detectChanges();
                    }
                },
                error: (err) => {
                    console.error(err);
                    this.toastDomainService.addToastMessage(
                        TypeToast.danger,
                        "Export Délégations",
                        "Erreur lors de l'export des délégations"
                    );
                    this.exportIsSubmitting = false;
                    this.cdr.detectChanges();
                }
            });
        } else {
            this.exportIsSubmitting = false;
            this.cdr.detectChanges();
        }
    }

    private getStartManagerCalendar(): Date {
        const today = new Date()
        return new Date(today.getFullYear() - 3, today.getMonth(), 1)
    }

    private getEndManagerCalendar(): Date {
        const today = new Date()
        return new Date(today.getFullYear(), today.getMonth() + 2, 0)
    }

    buildColumnDefs(): void {
        const newColumnDefs: ColDef[] = [
            {
                headerName: 'Collaborateur délégataire',
                headerTooltip: 'Collaborateur délégataire',
                valueGetter: (params) => params.data.delegataire,
                tooltipValueGetter: (params) => params.data.delegataire,
                flex: 1
            },
            {
                headerName: 'Date début délégation',
                headerTooltip: 'Date début délégation',
                valueGetter: (params) => {
                    if(params.data && params.data.dateDebutDelegation != null) {
                        return dayjs(params.data.dateDebutDelegation).format('DD/MM/YYYY')
                    }
                    return ''
                },
                flex: 1
            },
            {
                headerName: 'Périmètre',
                headerTooltip: 'Périmètre',
                valueGetter: (params) => params.data.perimetreDelegation,
                tooltipValueGetter: (params) => params.data.perimetreDelegation,
                flex: 1
            },
            {
                headerName: 'Date début périmètre',
                headerTooltip: 'Date début périmètre',
                valueGetter: (params) => {
                    if(params.data && params.data.dateDebutPerimetre != null) {
                        return dayjs(params.data.dateDebutPerimetre).format('DD/MM/YYYY')
                    }
                    return ''
                },
                flex: 1
            }
        ]

        // pour déterminer les en-têtes des colonnes dynamiques.
        if (this.delegations && this.delegations.length > 0 && this.additionalColumns.length > 0) {
            this.additionalColumns.forEach((valeurColonne, index) => {
                newColumnDefs.push({
                    headerName: 'Manager N+' + valeurColonne,
                    headerTooltip: 'Manager N+' + valeurColonne,
                    valueGetter: (params: ValueGetterParams) => {
                        if (params.data.managersHierarchique && params.data.managersHierarchique[index]) {
                            const valideurLigneActuelle = params.data.managersHierarchique[index];
                            return `${valideurLigneActuelle || ''}`.trim();
                        }
                        return '';
                    },
                    tooltipValueGetter: (params: ITooltipParams) => {
                        if (params.data.managersHierarchique && params.data.managersHierarchique[index]) {
                            const valideurLigneActuelle = params.data.managersHierarchique[index];
                            return `${valideurLigneActuelle || ''}`.trim();
                        }
                        return '';
                    },
                    flex: 1
                });
            });
        }

        // Modifier la dernière colonne pour qu'elle ne soit pas redimensionnable
        if (newColumnDefs.length > 0) {
            newColumnDefs[newColumnDefs.length - 1].resizable = false;
        }

        this.colDefs = [...newColumnDefs];

    }


    protected readonly localeFr = localeFr;
}
